import { createTheme as createMuiTheme } from '@mui/material/styles'
import { camelizeKeys } from 'humps'
import qs from 'qs'

// partner branding
const params = camelizeKeys(
  qs.parse(window.location.search, { ignoreQueryPrefix: true })
)
const brandingPrimary = params.brandingColorPrimary
  ? params.brandingColorPrimary.startsWith('#')
    ? params.brandingColorPrimary
    : `#${params.brandingColorPrimary}`
  : null
const brandingSecondary = params.brandingColorSecondary
  ? params.brandingColorSecondary.startsWith('#')
    ? params.brandingColorSecondary
    : `#${params.brandingColorSecondary}`
  : null

// intelibly branding
const INTELIBLY_COLORS = {
  primary: {
    50: 'rgba(251, 247, 252, 1)',
    100: 'rgba(246, 238, 249, 1)',
    200: 'rgba(236, 219, 243, 1)',
    300: 'rgba(223, 191, 232, 1)',
    400: 'rgba(205, 153, 217, 1)',
    500: 'rgba(180, 112, 197, 1)',
    600: 'rgba(153, 81, 168, 1)',
    700: 'rgba(140, 71, 153, 1)',
    800: 'rgba(105, 54, 114, 1)',
    900: 'rgba(89, 48, 95, 1)',
    950: 'rgba(55, 22, 60, 1)',
  },
  secondary: {
    50: 'rgba(243, 244, 251, 1)',
    100: 'rgba(227, 231, 246, 1)',
    200: 'rgba(206, 215, 239, 1)',
    300: 'rgba(173, 188, 227, 1)',
    400: 'rgba(133, 154, 213, 1)',
    500: 'rgba(104, 123, 201, 1)',
    600: 'rgba(85, 98, 187, 1)',
    700: 'rgba(74, 81, 171, 1)',
    800: 'rgba(65, 69, 140, 1)',
    900: 'rgba(56, 60, 112, 1)',
    950: 'rgba(38, 39, 69, 1)',
  },
  neutral: {
    50: 'rgba(248, 249, 250, 1)',
    100: 'rgba(242, 243, 245, 1)',
    200: 'rgba(231, 232, 237, 1)',
    300: 'rgba(211, 211, 223, 1)',
    400: 'rgba(185, 185, 204, 1)',
    500: 'rgba(150, 149, 176, 1)',
    600: 'rgba(136, 133, 162, 1)',
    700: 'rgba(117, 114, 143, 1)',
    800: 'rgba(98, 95, 120, 1)',
    900: 'rgba(82, 79, 99, 1)',
    950: 'rgba(53, 52, 65, 1)',
  },
}

export const createInteliblyTheme = (overrides = {}) => {
  const primaryMain = brandingPrimary || INTELIBLY_COLORS.primary[700]
  const secondaryMain = brandingSecondary || INTELIBLY_COLORS.secondary[700]

  const theme = createMuiTheme({
    components: {
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: '24px',
          },
        },
      },
    },
    palette: {
      primary: {
        main: primaryMain,
      },
      secondary: {
        main: secondaryMain,
      },
      neutral: INTELIBLY_COLORS.neutral,
    },
    typography: {
      fontFamily: ['Inter', 'Helvetica'].join(','),
      h5: {
        fontWeight: 600,
        color: INTELIBLY_COLORS.neutral[900],
      },
      h6: {
        color: INTELIBLY_COLORS.neutral[700],
        fontWeight: 600,
      },
      body1: {
        color: INTELIBLY_COLORS.neutral[900],
      },
      body2: {
        color: INTELIBLY_COLORS.neutral[900],
        fontWeight: 500,
      },
      button: {
        color: primaryMain,
      },
      subtitle1: {
        color: INTELIBLY_COLORS.neutral[900],
      },
      subtitle2: {
        color: secondaryMain,
        lineHeight: 1.334,
      },
    },
    ...overrides,
  })

  // preserve custom shade system and let MUI handle standard colors
  theme.palette.primary = {
    ...theme.palette.primary,
    ...INTELIBLY_COLORS.primary,
    main: theme.palette.primary.main,
  }

  theme.palette.secondary = {
    ...theme.palette.secondary,
    ...INTELIBLY_COLORS.secondary,
    main: theme.palette.secondary.main,
  }

  return theme
}

export const generateGlobalCSS = (theme = null) => {
  const variables = []
  const finalTheme = theme || createInteliblyTheme()

  Object.entries(INTELIBLY_COLORS.neutral).forEach(([shade, color]) => {
    variables.push(`--intelibly-branding-neutral-${shade}: ${color};`)
  })

  // use MUI's generated colors for primary
  Object.entries(INTELIBLY_COLORS.primary).forEach(([shade, color]) => {
    let finalColor = color
    if (shade === '700') {
      finalColor = brandingPrimary || color
    } else if (finalTheme.palette.primary[shade]) {
      finalColor = finalTheme.palette.primary[shade]
    }
    variables.push(`--intelibly-branding-primary-${shade}: ${finalColor};`)
  })

  variables.push(
    `--intelibly-branding-primary-main: ${finalTheme.palette.primary.main};`
  )
  variables.push(
    `--intelibly-branding-primary-light: ${finalTheme.palette.primary.light};`
  )
  variables.push(
    `--intelibly-branding-primary-dark: ${finalTheme.palette.primary.dark};`
  )

  // use MUI's generated colors for secondary
  Object.entries(INTELIBLY_COLORS.secondary).forEach(([shade, color]) => {
    let finalColor = color
    if (shade === '700') {
      finalColor = brandingSecondary || color
    } else if (finalTheme.palette.secondary[shade]) {
      finalColor = finalTheme.palette.secondary[shade]
    }
    variables.push(`--intelibly-branding-secondary-${shade}: ${finalColor};`)
  })

  variables.push(
    `--intelibly-branding-secondary-main: ${finalTheme.palette.secondary.main};`
  )
  variables.push(
    `--intelibly-branding-secondary-light: ${finalTheme.palette.secondary.light};`
  )
  variables.push(
    `--intelibly-branding-secondary-dark: ${finalTheme.palette.secondary.dark};`
  )

  // intelibly package styles
  return `
    :root {
      ${variables.join('\n      ')}
    }

    * { 
      box-sizing: border-box; 
    }  

    html,
    body { 
      margin: 0;
      padding: 0;
      height: 100%;
    }

    body {
      font-family: system-ui, sans-serif;
      -webkit-font-smoothing: antialiased;
      -moz-osx-font-smoothing: grayscale;
    }

    body > * {
      -webkit-overflow-scrolling: touch;
    }

    button:focus-visible {
      outline: 2px solid #4a90e2 !important;
      outline: -webkit-focus-ring-color auto 5px !important;
    }

    a {
      text-decoration: none;
    }
  `
}

export const inteliblyTheme = createInteliblyTheme()
